/* eslint-disable complexity */
import getValidEndDate from 'utils/pivot/date/getValidEndDate'
import lastDateByPeriod from 'utils/pivot/date/lastDateByPeriod'
import { IS_ENROLL } from 'core/constants/environment'
import getPeriodRange from 'utils/pivot/date/getPeriodRange'
import { IS_AGENT } from 'core/constants/environment'
import {
  alterDate,
  stringToDate,
  getDifferenceOnDays,
  findFirstOccurrence
} from '@hc-frontend/utils'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import moneyFormat from 'utils/format/money'
import getValueFees from './getValueFees'
import getPremium from './getPremium'
import {
  DENTAL,
  VISION,
  LATITUDE,
  SUPPLEMENTAL_BENEFIT,
  AMERITAS_VISION,
  ALL_DENTAL,
  ALL_VISION
} from 'core/constants/products'
import getExtendDuration from 'utils/pivot/plan/getExtendDuration'
import getAndValidatePeriod from 'utils/pivot/plan/getAndValidateDuration'
import getValidPeriodKey from 'utils/pivot/plan/period/getValidPeriodKey'
import { findApplicant } from '@hc-frontend/business'
import { propAccessor } from '@hc-frontend/utils'
import { Decimal } from 'decimal.js'
import ascSort from 'utils/ascSort'

const toMoney = moneyFormat

export function showExtended(
  sDate: Date,
  rangePolicies: string[],
  period: number,
  applicant: any,
  state_acronym: string,
  plan?: Pivot.Plan
) {
  if (plan && isBTMPlan(plan)) return false

  if (rangePolicies.length === 1) {
    return false
  }
  // Don't show extended plan on Enroll site for 90 days policy.
  // Note: 2x90 plans display on Enroll site only for VA
  if (!IS_AGENT && period === 90) {
    return state_acronym === 'VA'
  }

  const endDate = lastDateByPeriod(sDate, +period)
  const years65 = alterDate({ date: applicant.dob, years: 65 })

  return getDifferenceOnDays(years65, endDate, true) < 0
}

export function showPrepaid(state, plan, applicant): boolean {
  const validatedPeriod = getAndValidatePeriod(plan, state.filters.period)
  const policy = plan?.multiCarrierPlan
    ? validatedPeriod
    : validatedPeriod[state.filters.policies]
  const endDateObj = stringToDate(state.endDate)
  const years65 = alterDate({ date: applicant.dob, years: 65 })

  // rules:
  // * Is prepaid available from endpoint
  // * The coverage should be at least 60 days
  // * The principal applicant must be under 65 at the end of the policy coverage.
  return (
    !!policy.prepaid &&
    state.periodRange.days >= 60 &&
    getDifferenceOnDays(years65, endDateObj, true) <= 0
  )
}

function applyRules(state: Pivot.State) {
  const applicant = findApplicant(state.hcStorage.members)
  const state_acronym = propAccessor(state, 'hcStorage.location.state_acronym')
  const plan = state.plans[state.planId]
  const isBTM = isBTMPlan(plan)
  const filters = state.filters
  const period = isBTM
    ? state.defaultPeriod
    : +getValidPeriodKey(plan, filters.period || state.defaultPeriod)
  const duration = plan?.multiCarrierPlan
    ? {
        1: getAndValidatePeriod(plan, period)
      }
    : getAndValidatePeriod(plan, period)
  const sDate = stringToDate(state.sDate)

  const rangePolicies = isBTM
    ? [plan.duration.recurrence]
    : Object.keys(duration).sort(ascSort)

  state.minPolicies = +rangePolicies[0]
  state.maxPolicies = +rangePolicies[rangePolicies.length - 1]
  state.showExtended = showExtended(
    sDate,
    rangePolicies,
    period,
    applicant,
    state_acronym,
    plan,
    state
  )
  state.period = period
  state.filters.period = period
  const policies = filters.policies
  state.isExtendedPlan = isBTM ? false : policies > 1
  state.hcStorage = {
    ...state.hcStorage,
    quoteKey: state.quoteKey
  }

  state.endDate = getValidEndDate({
    startDate: sDate,
    endDate: state.endDate,
    period,
    isExtendedPlan: state.isExtendedPlan,
    isBTM,
    policies,
    dob: applicant.dob
  })

  state.periodRange = getPeriodRange({
    extendedDuration: getExtendDuration(plan, state.period),
    period,
    sDate,
    endDate: state.endDate,
    policies
  })

  state.planName = `${plan.planName}${state.isExtendedPlan ? ' Added Coverage' : ''}`

  // dental and vision section
  state.dentalPlanId = findFirstOccurrence(
    state.addOns,
    ALL_DENTAL.flatMap(productId => state.plansByType[productId])
  )
  state.visionPlanId = findFirstOccurrence(
    state.addOns,
    ALL_VISION.flatMap(productId => state.plansByType[productId])
  )

  // latitude section
  state.latitudeId = findFirstOccurrence(state.addOns, state.plansByType[LATITUDE])
  const latitudeCost = getPremium(state.plans[state.latitudeId])
  state.showPrepaid = isBTM ? false : showPrepaid(state, plan, applicant)
  state.typeOfPay = state.showPrepaid ? state.typeOfPay : 'monthly'

  // supplemental NRIC

  state.supplementalId = IS_ENROLL
    ? undefined
    : findFirstOccurrence(state.addOns, state.plansByType[SUPPLEMENTAL_BENEFIT])

  const supplementalCost =
    (state.supplementalId &&
      state.plans[state.supplementalId].durations[state.period][policies][state.typeOfPay]
        .premium) ||
    0

  state.addOns = [
    state.dentalPlanId,
    state.visionPlanId,
    state.latitudeId,
    state.supplementalId
  ].filter(Boolean)

  state.addonsByPlan[state.planId] = state.addOns

  const dentalPlan = state.plans[state.dentalPlanId]
  const visionPlan = state.plans[state.visionPlanId]
  if (
    dentalPlan &&
    visionPlan &&
    dentalPlan.productId === DENTAL &&
    visionPlan.productId === VISION
  ) {
    state.dentalVisionTitle = `${dentalPlan.planName} + Vision`
  } else {
    state.dentalVisionTitle = dentalPlan?.planName
  }

  // prices section
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paidInformationPlan: any =
    duration[plan.multiCarrierPlan ? '1' : policies][state.typeOfPay] // For multiCarrierPlan, the policies number is always 1
  const subscriptionFee = new Decimal(
    getValueFees('Application Fee', paidInformationPlan.fees)
  )

  let dentalCost = new Decimal(getPremium(state.plans[state.dentalPlanId]))
  if (
    state.dentalPlanId &&
    state.visionPlanId &&
    dentalPlan.productId === DENTAL &&
    visionPlan.productId === VISION &&
    !state.hasDentalActive &&
    !state.hasVisionActive
  ) {
    dentalCost = dentalCost.plus(new Decimal(getPremium(state.plans[state.visionPlanId])))
  }

  const visionCost =
    visionPlan?.productId === AMERITAS_VISION
      ? new Decimal(getPremium(visionPlan))
      : new Decimal(0)

  const total = Decimal.sum(
    new Decimal(paidInformationPlan.premium),
    dentalCost,
    new Decimal(latitudeCost || 0),
    new Decimal(supplementalCost || 0),
    visionCost
  )

  state.prices = {
    plan: toMoney(paidInformationPlan.premium),
    planNumber: paidInformationPlan.premium,
    dental: toMoney(dentalCost.toNumber()),
    latitude: toMoney(latitudeCost),
    supplemental: toMoney(supplementalCost),
    vision: toMoney(visionCost.toNumber()),
    total: toMoney(total.toNumber()),
    subscriptionFee: toMoney(subscriptionFee.toNumber()),
    subscriptionFeeNumber: subscriptionFee.toNumber(),
    bigTotal: toMoney(total.plus(subscriptionFee).toNumber())
  }

  return state
}

export default applyRules
